import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';

/**
 * Matches the BudgetState returned by GET /api/budget (see
 * review-service/src/budget/budget-state.ts). Keep fields in sync with the
 * backend — extra fields are ignored so additions are non-breaking.
 */
export interface BudgetStateData {
  dailyLimitUsd: number;
  monthlyLimitUsd: number;
  dailySpentUsd: number;
  monthlySpentUsd: number;
  circuitOpen: boolean;
  overrideReason?: string;
}

type Severity = 'green' | 'yellow' | 'red';

function severityForUtilization(utilization: number, circuitOpen: boolean): Severity {
  if (circuitOpen || utilization >= 0.9) return 'red';
  if (utilization >= 0.7) return 'yellow';
  return 'green';
}

function combinedSeverity(budget: BudgetStateData): Severity {
  const daily = budget.dailyLimitUsd > 0 ? budget.dailySpentUsd / budget.dailyLimitUsd : 0;
  const monthly = budget.monthlyLimitUsd > 0 ? budget.monthlySpentUsd / budget.monthlyLimitUsd : 0;
  const worst = Math.max(daily, monthly);
  return severityForUtilization(worst, budget.circuitOpen);
}

const SEVERITY_CLASS: Record<Severity, string> = {
  green: 'bg-green-900/30 border-green-700 text-green-300',
  yellow: 'bg-yellow-900/30 border-yellow-700 text-yellow-300',
  red: 'bg-red-900/30 border-red-700 text-red-300',
};

/** 30 seconds — banner polls infrequently to keep request volume low. */
const POLL_INTERVAL_MS = 30_000;

interface CostBannerProps {
  /** Render as a compact inline pill suitable for the page header row. */
  compact?: boolean;
}

export function CostBanner({ compact = false }: CostBannerProps = {}) {
  const [budget, setBudget] = useState<BudgetStateData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    async function fetchBudget() {
      try {
        const data = await api.budget.get();
        if (!mountedRef.current) return;
        setBudget(data);
        setError(null);
      } catch (err) {
        if (!mountedRef.current) return;
        // 503 when BUDGET_DO / REVIEW_SERVICE binding is absent during migration
        // (review-service returns 503 via bucketMissingResponse(), and the api
        // worker proxy also returns 503 when REVIEW_SERVICE is not configured).
        // Treat as "missing state" rather than an error so the banner can render
        // a neutral placeholder instead of an error card.
        if (err instanceof ApiError && err.status === 503) {
          setBudget(null);
          setError(null);
        } else {
          setError(err instanceof Error ? err.message : String(err));
        }
      }
    }
    fetchBudget();
    const interval = setInterval(fetchBudget, POLL_INTERVAL_MS);
    return () => {
      mountedRef.current = false;
      clearInterval(interval);
    };
  }, []);

  const sizeClass = compact
    ? 'gap-2 px-3 py-1 text-xs'
    : 'flex-wrap gap-4 px-4 py-2 text-sm';

  if (error) {
    return (
      <div
        data-testid="cost-banner"
        className={`rounded-lg border border-gray-700 bg-gray-900 text-gray-400 ${sizeClass}`}
      >
        Budget status unavailable
      </div>
    );
  }

  if (!budget) {
    return (
      <div
        data-testid="cost-banner"
        className={`rounded-lg border border-gray-700 bg-gray-900 text-gray-500 ${sizeClass}`}
      >
        Loading budget…
      </div>
    );
  }

  const severity = combinedSeverity(budget);
  // Compact pill keeps limits at integer precision for width, but preserves
  // cents on the spent amount so sub-dollar spend doesn't render as "$0".
  const dailyLabel = compact
    ? `$${budget.dailySpentUsd.toFixed(2)}/$${budget.dailyLimitUsd.toFixed(0)} today`
    : `Today: $${budget.dailySpentUsd.toFixed(2)} / $${budget.dailyLimitUsd.toFixed(2)}`;
  const monthlyLabel = compact
    ? `$${budget.monthlySpentUsd.toFixed(2)}/$${budget.monthlyLimitUsd.toFixed(0)} month`
    : `This month: $${budget.monthlySpentUsd.toFixed(2)} / $${budget.monthlyLimitUsd.toFixed(2)}`;

  return (
    <Link
      to="/costs"
      data-testid="cost-banner"
      data-severity={severity}
      data-compact={compact ? 'true' : 'false'}
      className={`inline-flex items-center rounded-lg border ${sizeClass} ${SEVERITY_CLASS[severity]}`}
    >
      <span data-testid="cost-banner-daily">{dailyLabel}</span>
      <span className="opacity-50">{compact ? '·' : '|'}</span>
      <span data-testid="cost-banner-monthly">{monthlyLabel}</span>
      {budget.circuitOpen && (
        <>
          <span className="opacity-50">{compact ? '·' : '|'}</span>
          <span data-testid="cost-banner-circuit" className="font-semibold uppercase tracking-wide">
            ⚠ Circuit Open
          </span>
        </>
      )}
    </Link>
  );
}
